import {Permission} from './permission';

/**
 * System roles within a firm, stored by key in `firm_user_roles.role_key` and
 * `firm_invitations.role_key`. Defined only in code — see `SYSTEM_ROLE_DEFINITIONS`.
 */
export enum Role {
  /**
   * Administrative owner of a firm. Reads every account in the firm, writes only
   * accounts they own, and manages team members.
   */
  FIRM_ADMIN = 'firm_admin',

  /**
   * A lawyer who creates and manages trust accounts end-to-end. Reads and writes
   * only the accounts they own — no visibility into other lawyers' accounts, no
   * team management.
   */
  FIRM_TRUSTEE = 'firm_trustee',

  /**
   * Read-only visibility across every account in the firm. Holds no write
   * permission — every mutating route is unreachable regardless of data scope.
   */
  FIRM_VIEWER = 'firm_viewer',

  /** No firm permission; works only on the accounts they are added to, as operator or viewer. */
  FIRM_MEMBER = 'firm_member',

  /**
   * Sollapay-internal technical support. Firm-wide read visibility plus two
   * operational writes — resending a payment instruction and inviting team members.
   * Never assignable by a firm admin: hidden from the role catalog and protected
   * from re-role/removal. See `assignableByFirmAdmin` on `SystemRoleDefinition`.
   */
  FIRM_SUPPORT = 'firm_support'
}

/**
 * Canonical display/sort rank for system roles, most privileged first. `Role` enum
 * declaration order happens to match this today, but call sites that need an explicit
 * order (sorting, list rendering) should read this rather than rely on that coincidence.
 */
export const ROLE_ORDER: Record<Role, number> = {
  [Role.FIRM_ADMIN]: 0,
  [Role.FIRM_TRUSTEE]: 1,
  [Role.FIRM_VIEWER]: 2,
  [Role.FIRM_MEMBER]: 3,
  [Role.FIRM_SUPPORT]: 4
};

/**
 * What a role is granted on. A `FIRM` role is a user's one role in the firm (`firm_user_roles`);
 * a `TRUST_ACCOUNT` role is granted on a single trust account (`trust_account_members`) and never
 * appears where firm roles are listed or assigned.
 */
export enum RoleScope {
  FIRM = 'firm',
  PLATFORM = 'platform',
  TRUST_ACCOUNT = 'trust_account'
}

/**
 * Data-scope axis for account-scoped domains (trust_accounts/buyers/payments/documents).
 * `readScope` gates GET list/detail; `writeScope` gates the ownership check on mutations.
 * `null` means the role holds no permission on that axis (e.g. `firm_viewer` has no
 * `writeScope` — it holds no write permission at all).
 */
export enum DataScope {
  OWN = 'own',
  FIRM = 'firm'
}

/**
 * A system role's full definition — the single source access resolution and the integrity
 * tests read. No display copy — the frontend looks up a system role's label/description via
 * `t(\`roles.${key}.label\`)` in en.json/he.json, same as permissions.
 */
export interface SystemRoleDefinition {
  scope: RoleScope;
  readScope: DataScope | null;
  writeScope: DataScope | null;
  /**
   * `false` means the role is Sollapay-assigned only: hidden from the role catalog
   * (`GET /roles`), un-assignable via invite or change-role, and protected from
   * re-role/removal once held. See `isFirmAssignableRole()`.
   */
  assignableByFirmAdmin: boolean;
  permissions: Permission[];
}

const ALL_FIRM_PERMISSIONS: Permission[] = Object.values(Permission);

/** Exported for role-integrity tests to assert against directly. */
export const READ_ONLY_PERMISSIONS: Permission[] = [
  Permission.TRUST_ACCOUNTS_READ,
  Permission.BUYERS_READ,
  Permission.PAYMENTS_READ,
  Permission.DOCUMENTS_READ,
  Permission.TEAM_READ
];

/** Firm-administration permissions a trustee never holds, even as new permissions are added. */
const FIRM_ADMINISTRATION_PERMISSIONS: Permission[] = [
  Permission.TEAM_MANAGE_MEMBERS,
  Permission.TEAM_INVITE_MEMBERS,
  Permission.FIRM_SETTINGS_UPDATE
];

/**
 * Canonical definition of every system role — permissions, and independent
 * read/write data scopes. Access is resolved from this at request time, never stored in the
 * DB; role-integrity unit tests assert against it directly.
 */
export const SYSTEM_ROLE_DEFINITIONS: Record<Role, SystemRoleDefinition> = {
  [Role.FIRM_ADMIN]: {
    scope: RoleScope.FIRM,
    readScope: DataScope.FIRM,
    writeScope: DataScope.OWN,
    assignableByFirmAdmin: true,
    permissions: ALL_FIRM_PERMISSIONS
  },
  [Role.FIRM_TRUSTEE]: {
    scope: RoleScope.FIRM,
    readScope: DataScope.OWN,
    writeScope: DataScope.OWN,
    assignableByFirmAdmin: true,
    permissions: ALL_FIRM_PERMISSIONS.filter(
      permission => !FIRM_ADMINISTRATION_PERMISSIONS.includes(permission)
    )
  },
  [Role.FIRM_VIEWER]: {
    scope: RoleScope.FIRM,
    readScope: DataScope.FIRM,
    writeScope: null,
    assignableByFirmAdmin: true,
    permissions: READ_ONLY_PERMISSIONS
  },
  [Role.FIRM_MEMBER]: {
    scope: RoleScope.FIRM,
    readScope: DataScope.OWN,
    writeScope: DataScope.OWN,
    assignableByFirmAdmin: true,
    permissions: [Permission.TEAM_READ]
  },
  [Role.FIRM_SUPPORT]: {
    scope: RoleScope.FIRM,
    readScope: DataScope.FIRM,
    // Support owns no trust accounts, so the ownership check must no-op like it does
    // for `firm`-scoped reads — otherwise `payments:resend` would be unreachable.
    writeScope: DataScope.FIRM,
    assignableByFirmAdmin: false,
    permissions: [
      ...READ_ONLY_PERMISSIONS,
      Permission.PAYMENTS_RESEND,
      Permission.TEAM_INVITE_MEMBERS
    ]
  }
};

/**
 * Whether a firm admin may assign/hold this role via self-service team management
 * (invite, change-role) — `false` for Sollapay-assigned roles like `firm_support`.
 * Defaults to `true` for keys absent from `SYSTEM_ROLE_DEFINITIONS` (phase-2 custom
 * roles), which carry no such restriction.
 */
export const isFirmAssignableRole = (roleKey: string): boolean =>
  SYSTEM_ROLE_DEFINITIONS[roleKey as Role]?.assignableByFirmAdmin ?? true;
